import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Chip, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { bank, errorText } from "./api";

/** Seconds left until expiresAt, ticking every second */
export function Countdown({ expiresAt, onExpire }) {
  const [left, setLeft] = useState(0);
  const fired = useRef(false);
  useEffect(() => {
    fired.current = false;
    const tick = () => {
      const seconds = Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setLeft(seconds);
      if (seconds === 0 && !fired.current) {
        fired.current = true;
        if (onExpire) onExpire();
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt, onExpire]);
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  return <Chip size="small" color={left > 20 ? "default" : "warning"} label={`${mm}:${ss}`} />;
}

/** Two-column evidence table */
export function Evidence({ rows }) {
  return (
    <Table size="small" sx={{ "& td": { borderBottom: "1px solid #eee", verticalAlign: "top" } }}>
      <TableBody>
        {rows
          .filter(([, value]) => value !== undefined && value !== null && value !== "")
          .map(([label, value]) => (
            <TableRow key={label}>
              <TableCell sx={{ width: 220, color: "text.secondary" }}>{label}</TableCell>
              <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>{String(value)}</TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}

/** Log of demo outcomes: expected refusals show as "Blocked" with the server's code */
export function OutcomeLog({ entries }) {
  if (!entries.length) return null;
  return (
    <Box sx={{ mt: 2 }}>
      {entries.map((entry, index) => (
        <Alert
          key={index}
          severity={entry.ok ? "success" : entry.blocked ? "info" : "error"}
          sx={{ mb: 1 }}
          icon={false}
        >
          <Typography variant="body2">
            <strong>{entry.ok ? "Allowed" : entry.blocked ? "Blocked (as expected)" : "Error"}:</strong> {entry.label}
          </Typography>
          {entry.detail && (
            <Typography variant="caption" sx={{ fontFamily: "monospace", display: "block" }}>
              {entry.detail}
            </Typography>
          )}
        </Alert>
      ))}
    </Box>
  );
}

/** Builds an OutcomeLog entry from an API response */
export const outcome = (label, response, expectRefusal = false) => {
  const ok = response.status >= 200 && response.status < 300;
  return {
    label,
    ok,
    blocked: !ok && expectRefusal,
    detail: ok ? "" : `HTTP ${response.status} ${errorText(response.data)}`,
  };
};

/**
 * Polls the bank's GET /payments/:id (ExposedApis GET /txn-sign/:id with the API secret)
 * every 2 seconds until the payment leaves PENDING / CHALLENGE_ISSUED.
 */
export function usePaymentStatus(requestId, onFinal) {
  const [status, setStatus] = useState(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;
  useEffect(() => {
    if (!requestId) return undefined;
    let stopped = false;
    const poll = async () => {
      const res = await bank.get(`/payments/${requestId}`);
      if (stopped) return;
      if (res.status === 200) {
        setStatus(res.data);
        if (!["PENDING", "CHALLENGE_ISSUED"].includes(res.data.status)) {
          stopped = true;
          if (onFinalRef.current) onFinalRef.current(res.data);
          return;
        }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [requestId]);
  return status;
}

/** "Approve on your phone" panel with a countdown */
export function PhonePrompt({ expiresAt, delivery = [], status }) {
  const failed = delivery.filter((d) => d.status !== "SENT");
  return (
    <Box sx={{ p: 2, border: "1px dashed #1976d2", borderRadius: 2, mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="body1">Waiting for approval on your phone</Typography>
        {expiresAt && <Countdown expiresAt={expiresAt} />}
        {status && <Chip label={status} color={status === "APPROVED" ? "success" : "default"} />}
      </Box>
      {delivery.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Push delivery: {delivery.map((d) => `${d.deviceName || d.deviceType || "device"} ${d.status}`).join(", ")}
          {failed.length > 0 && " - push not delivered; open the approver with the request id."}
        </Typography>
      )}
    </Box>
  );
}
